/* eslint-disable no-console */
/**
 * Background Sync Utilities
 *
 * Provides utilities for registering background sync with the service worker.
 * This allows mutations to be synced even when the app is closed or in the background.
 */

/**
 * Check if Background Sync API is supported
 */
export function isBackgroundSyncSupported(): boolean {
  return 'serviceWorker' in navigator && 'SyncManager' in window;
}

/**
 * Get the active service worker registration
 */
async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.warn('Failed to get service worker registration:', error);
    return null;
  }
}

/**
 * Register a background sync with the service worker
 * This will trigger a sync event when the device comes back online
 */
export async function requestBackgroundSync(): Promise<boolean> {
  const registration = await getServiceWorkerRegistration();

  if (!registration) {
    console.warn('No service worker registration available');
    return false;
  }

  // Try using the Background Sync API directly
  if ('sync' in registration) {
    try {
      await (
        registration as ServiceWorkerRegistration & { sync: { register: (tag: string) => Promise<void> } }
      ).sync.register('offline-mutations-sync');
      return true;
    } catch (error) {
      console.warn('Background sync registration failed, falling back to message:', error);
    }
  }

  // Fallback: Send a message to the service worker to register sync
  try {
    if (registration.active) {
      registration.active.postMessage({ type: 'REGISTER_SYNC' });
      return true;
    }
  } catch (error) {
    console.warn('Failed to send sync message to service worker:', error);
  }

  return false;
}

/**
 * Trigger an immediate sync via the service worker
 * Use this when the app knows it's online and wants to sync now
 */
export async function triggerImmediateSync(): Promise<void> {
  const registration = await getServiceWorkerRegistration();

  if (registration?.active) {
    registration.active.postMessage({ type: 'TRIGGER_SYNC' });
  }
}

/**
 * Subscribe to sync messages from the service worker
 * Returns an unsubscribe function
 */
export function subscribeToSyncMessages(callback: (event: MessageEvent) => void): () => void {
  const handler = (event: MessageEvent) => {
    if (event.data && event.data.type === 'SYNC_MUTATIONS') {
      callback(event);
    }
  };

  navigator.serviceWorker?.addEventListener('message', handler);

  return () => {
    navigator.serviceWorker?.removeEventListener('message', handler);
  };
}
